import React from 'react';
import PropTypes from 'prop-types';

import {
  fnChildrenPropType,
  fnElementRefPropType,
} from 'utils/customPropTypes';
import oIconLibrary from 'utils/iconLibrary';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/**
 * Displays a regular, text-only button.
 *
 * @param {string} sFlavor - Button variation.
 * @param {function} fnHandleClick - Click handler.
 * @param {boolean} bIsDisabled - If the button is disabled or not.
 */
const Button = ({
  sFlavor,
  fnHandleClick,
  children,
  bIsDisabled,
  className,
  bIsLoading,
  sType,
  bIsCentered,
  oRef,
  sCypressId,
}) => {
  const sCenteredClass = bIsCentered ? 'button--centered' : '';
  return (
    <button
      // eslint-disable-next-line react/button-has-type
      type={sType}
      className={`button--${sFlavor} ${sCenteredClass} ${className}`}
      onClick={fnHandleClick}
      disabled={bIsDisabled || bIsLoading}
      ref={oRef}
      data-cy={sCypressId}
    >
      {bIsLoading ? (
        <>
          <FontAwesomeIcon icon={oIconLibrary.fontAwesome.spinner} spin />
          <span className='hide_accessibly'>Loading</span>
        </>
      ) : (
        children
      )}
    </button>
  );
};

Button.propTypes = {
  sFlavor: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'underline']),
  fnHandleClick: (props, propName) => {
    // This prop is only required for regular buttons.
    // Formik auto-magically handles this for submit and reset buttons.
    if (props.sType === 'button') {
      if (!props[propName]) {
        return new Error(`${propName} is required for a regular button`);
      }
    }

    if (typeof props[propName] !== 'function') {
      return new Error(`${propName} must be a function`);
    }

    return null;
  },
  bIsDisabled: PropTypes.bool,
  children: fnChildrenPropType.isRequired,
  className: PropTypes.string,
  bIsLoading: PropTypes.bool,
  sType: PropTypes.oneOf(['button', 'submit', 'reset']),
  bIsCentered: PropTypes.bool,
  oRef: fnElementRefPropType,
  sCypressId: PropTypes.string,
};

Button.defaultProps = {
  sFlavor: 'primary',
  fnHandleClick: () => {},
  bIsDisabled: false,
  className: '',
  bIsLoading: false,
  sType: 'button',
  bIsCentered: false,
  oRef: null,
  sCypressId: '',
};

export default Button;
